import * as React from "react";
import type { LocationDto, SaveLocationRequest, LocationType } from "../../models/Locations";
import { useUser } from "../UserContext";
import { useApiClient } from "../useApiClient";
import { useIsAuthenticated } from "@azure/msal-react";
import { buildTree, type LocationNode } from "./locationTree.utils";
import { getApiErrorMessage } from "./apiError.utils";

export type LocationInput = {
    locationName: string;
    locationTypeId: number;
    parentLocationId: number | null;
};

/**
 * Owns all data + API concerns for the location admin page: fetching,
 * create/update/delete, and the derived tree. Pulled out of the page
 * component so the UI layer only has to deal with layout and selection
 * state.
 *
 * Fixes vs. the previous implementation:
 *  - `reload` is awaited everywhere it's called, so `loading` actually
 *    reflects an in-flight request instead of flashing true/false
 *    synchronously before the fetch resolves.
 *  - create/update/delete return promises that reject on failure, so the
 *    caller (the details panel) can catch and keep the form populated
 *    instead of silently clearing on a failed save.
 *  - the delete endpoint path is consistent with create/update (no stray
 *    leading slash), matching how the shared api client joins paths.
 */
export function useLocationsAdmin() {
    const { activeClientId, getAccessToken } = useUser();
    const { get, postJson, putJson, delete: del } = useApiClient();
    const isAuthenticated = useIsAuthenticated();

    const [locations, setLocations] = React.useState<LocationDto[]>([]);
    const [locationTypes, setLocationTypes] = React.useState<LocationType[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [status, setStatus] = React.useState("");

    const reload = React.useCallback(async () => {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        const [locationsData, locationTypesData] = await Promise.all([
            get<LocationDto[]>("locations/tree", accessToken),
            get<LocationType[]>("locations/types", accessToken),
        ]);
        setLocations(locationsData);
        setLocationTypes(locationTypesData);
    }, [get, getAccessToken]);

    // `reload`'s identity can change on every render if `get`/`getAccessToken`
    // aren't referentially stable (they come from useApiClient()/useUser()).
    // Route the initial-load effect through a ref, updated on every render,
    // instead of listing `reload` as a dependency — otherwise a changing
    // `reload` re-triggers the effect, which sets state, which re-renders,
    // which produces a new `reload`... an infinite fetch loop.
    const reloadRef = React.useRef(reload);
    reloadRef.current = reload;

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError("");
            try {
                await reloadRef.current();
            } catch (e: any) {
                if (!cancelled) setError(e?.message ?? "Unable to load locations");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Intentionally NOT depending on `reload` — see comment above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, activeClientId]);

    const createLocation = React.useCallback(
        async (input: LocationInput) => {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error("Not authenticated");
            const body: SaveLocationRequest = {
                locationName: input.locationName.trim(),
                locationTypeId: input.locationTypeId,
                parentLocationId: input.parentLocationId,
                clientId: activeClientId,
            };
            const created = await postJson<LocationDto>("locations/create", accessToken, body);
            await reload();
            setStatus(`"${created.locationName}" created`);
            return created;
        },
        [activeClientId, getAccessToken, postJson, reload]
    );

    const updateLocation = React.useCallback(
        async (locationId: number, input: LocationInput) => {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error("Not authenticated");
            const body: SaveLocationRequest = {
                locationId,
                locationName: input.locationName.trim(),
                locationTypeId: input.locationTypeId,
                parentLocationId: input.parentLocationId,
                clientId: activeClientId,
            };
            await putJson(`locations/${locationId}`, accessToken, body);
            await reload();
            setStatus(`"${input.locationName.trim()}" updated`);
        },
        [activeClientId, getAccessToken, putJson, reload]
    );

    /**
     * Reparent a single node via drag-and-drop without opening the form.
     * Unlike `updateLocation`, this catches its own errors (and surfaces
     * them through `error`) since drag-and-drop has no form to report
     * a failure into.
     */
    const moveLocation = React.useCallback(
        async (locationId: number, newParentId: number | null) => {
            const current = locations.find((l) => l.locationId === locationId);
            if (!current) return;
            try {
                await updateLocation(locationId, {
                    locationName: current.locationName,
                    locationTypeId: current.locationTypeId,
                    parentLocationId: newParentId,
                });
            } catch (e: any) {
                setError(getApiErrorMessage(e, `Couldn't move "${current.locationName}"`));
            }
        },
        [locations, updateLocation]
    );

    const deleteLocation = React.useCallback(
        async (locationId: number) => {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error("Not authenticated");
            await del(`locations/${locationId}`, accessToken);
            await reload();
            setStatus("Location removed");
        },
        [del, getAccessToken, reload]
    );

    const tree = React.useMemo<LocationNode[]>(() => buildTree(locations), [locations]);

    return {
        loading,
        error,
        clearError: () => setError(""),
        status,
        clearStatus: () => setStatus(""),
        locations,
        locationTypes,
        tree,
        reload,
        createLocation,
        updateLocation,
        moveLocation,
        deleteLocation,
    };
}
